import React, { useState } from "react";
import PropTypes from "prop-types";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextInput from "../../molecules/forms/inputs/TextInput";
import EmailInput from "../../molecules/forms/inputs/EmailInput";
import CheckboxInput from "../../molecules/forms/inputs/CheckboxInput";
import ReCAPTCHAInput from "../../molecules/forms/inputs/ReCAPTCHAInput";
import { gridClasses } from "../../../Helpers/GridClasses";
import drupalSettings from "../../../Helpers/drupalSettings";
import Message from "../../molecules/message/Message";
import communicationPreferencesContent from "../../../Pages/communication-preferences/content";

const lang = drupalSettings.path.currentLanguage;
const reCaptchaKey = drupalSettings.ed_dashboard.recaptcha_site_key;
const brandName = drupalSettings.ed_dashboard.brand_name;
const privacyLink = drupalSettings.ed_dashboard.privacy_link;
const marketingEnabled = drupalSettings.ed_dashboard.marketing_enabled;
const mandatoryPrivacyPolicyOptInMarkets = Object.keys(
  drupalSettings.ed_dashboard.mandatory_privacy_opt_in_markets
    ? drupalSettings.ed_dashboard.mandatory_privacy_opt_in_markets
    : {}
);

function EmailSignupForm({ client, onComplete }) {
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = (values, actions) => {
    let optIn = true;
    if (marketingEnabled) {
      optIn =
        values.marketingOptIn.length > 0 && values.marketingOptIn[0] === "1";
    }

    const currentMarket =
      localStorage.getItem("deliveryLocation") ||
      drupalSettings.regionMarket.market ||
      drupalSettings.regionMarket.location;

    const privacyPolicyOptIn = mandatoryPrivacyPolicyOptInMarkets.includes(
      currentMarket
    )
      ? values.privacyPolicyOptIn[0] === "1"
      : null;

    const postData = {
      emailAddress: values.email,
      firstName: values.givenName,
      lastName: values.familyName,
      optIn,
      privacyPolicyOptIn,
      language: lang,
      preferences: communicationPreferencesContent["Highland Park"].optInAPI,
      ...(values.reCAPTCHA && { token: values.reCAPTCHA })
    };

    client
      .post("Consumer/newsletter/signuprequest", postData)
      .then(() => {
        setSubmitted(true);
      })
      .catch(() => {
        setErrorMessage(
          <Message type="error">
            {window.Drupal.t("A problem has occurred. Please try again later.")}
          </Message>
        );
        actions.setSubmitting(false);
      });
  };

  const contentGridClass = gridClasses.full;

  if (submitted) {
    return (
      <div className={gridClasses.container}>
        <div className={gridClasses.row}>
          <div className={`${contentGridClass} text-center`}>
            <h1>{window.Drupal.t("Thank you")}</h1>
          </div>
        </div>
        <div className={gridClasses.row}>
          <div className={`${contentGridClass} text-center`}>
            <p>{window.Drupal.t("Your signup request has been received.")}</p>
          </div>
        </div>
        <div className={`signup-actions ${gridClasses.row}`}>
          <div className={`${contentGridClass} text-center`}>
            <div className="signup-buttons__container">
              {onComplete ? (
                <button
                  type="button"
                  className="button button--tertiary"
                  onClick={onComplete}
                >
                  {window.Drupal.t("Close")}
                </button>
              ) : (
                <a className="button button--tertiary" href={`/${lang}`}>
                  {window.Drupal.t("Back to home")}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className={gridClasses.row}>
        <div className={`${contentGridClass} text-center`}>
          <h1>{window.Drupal.t("Sign up to our newsletter")}</h1>
          <p>
            {window.Drupal.t(
              "By providing your information below, you are opting in to receive email communications from @brandName. These may include new product launches, upcoming events, experiences, partnerships, regional events, and distillery updates. You can review and update your preferences at any time.",
              { "@brandName": brandName }
            )}
          </p>
        </div>
      </div>

      {errorMessage && (
        <div className={gridClasses.row}>
          <div className={contentGridClass}>{errorMessage}</div>
        </div>
      )}

      <Formik
        initialValues={{
          givenName: "",
          familyName: "",
          email: "",
          marketingOptIn: [],
          privacyPolicyOptIn: [],
          reCAPTCHA: null
        }}
        validationSchema={Yup.object().shape({
          givenName: Yup.string()
            .required(window.Drupal.t("First name is required"))
            .max("50", window.Drupal.t("First name is too long")),
          familyName: Yup.string()
            .required(window.Drupal.t("Last name is required"))
            .max("50", window.Drupal.t("Last name is too long")),
          email: Yup.string()
            .email(window.Drupal.t("Email is not valid"))
            .required(window.Drupal.t("Email is required"))
            .matches(/^[^+]*$/, window.Drupal.t("Email is not valid")),
          marketingOptIn: Yup.string().when([], {
            is: () => marketingEnabled,
            then: Yup.string().required(
              window.Drupal.t("Please accept the marketing terms to continue.")
            )
          }),
          privacyPolicyOptIn: Yup.array().test(
            "valid-privacy-opt-in",
            window.Drupal.t("Please accept the privacy policy to continue."),
            value => {
              if (
                mandatoryPrivacyPolicyOptInMarkets.includes(
                  localStorage.getItem("deliveryLocation") ||
                    drupalSettings.regionMarket.market ||
                    drupalSettings.regionMarket.location
                )
              ) {
                return value[0] === "1";
              }
              return true;
            }
          ),
          reCAPTCHA: Yup.string()
            .nullable()
            .test(
              "recaptcha-required-if-enabled",
              window.Drupal.t("Please confirm you are not a robot."),
              value => {
                if (reCaptchaKey) {
                  return !!value;
                }
                return true;
              }
            )
        })}
        onSubmit={handleSubmit}
        render={formikProps => (
          <Form>
            <div className={gridClasses.row}>
              <div className={contentGridClass}>
                <TextInput
                  name="givenName"
                  label={window.Drupal.t("First name")}
                  required
                />
              </div>
            </div>
            <div className={gridClasses.row}>
              <div className={contentGridClass}>
                <TextInput
                  name="familyName"
                  label={window.Drupal.t("Last name")}
                  required
                />
              </div>
            </div>
            <div className={gridClasses.row}>
              <div className={contentGridClass}>
                <EmailInput
                  name="email"
                  label={window.Drupal.t("Email")}
                  required
                />
              </div>
            </div>
            <div className={gridClasses.row}>
              <div className={contentGridClass}>
                <p
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: window.Drupal.t(
                      "By entering and submitting my information, I give my consent to @brandName to process my personal data in accordance with their  <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a> and to receive email correspondence from them. You have the option to withdraw your consent at any time, as outlined in our <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a> or by contacting us at <a href='@mailto' target='_blank' rel='nofollow noreferrer'>hello@highlandparkwhisky.com</a>.",
                      {
                        "@brandName": brandName,
                        "@privacyPolicyURL": `/${lang}${privacyLink}`,
                        "@mailto": "mailto:hello@highlandparkwhisky.com"
                      }
                    )
                  }}
                />
              </div>
            </div>
            {marketingEnabled && (
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <CheckboxInput
                    name="marketingOptIn"
                    id="marketingOptIn"
                    label={window.Drupal.t(
                      "By signing up to the Inner Circle you are creating an account and opting into receiving email communications from Highland Park and its regional entities, which include global and regional communications. These include brand campaigns, new product releases, events, and regional specific communications."
                    )}
                    required
                    value="1"
                  />
                </div>
              </div>
            )}
            {mandatoryPrivacyPolicyOptInMarkets.includes(
              localStorage.getItem("deliveryLocation") ||
                drupalSettings.regionMarket.market ||
                drupalSettings.regionMarket.location
            ) && (
              <div className={gridClasses.row}>
                <div className={contentGridClass}>
                  <CheckboxInput
                    name="privacyPolicyOptIn"
                    id="privacyPolicyOptIn"
                    label={window.Drupal.t(
                      "Please tick to confirm you have read and agree to the processing of your personal data as set out in our <a href='@privacyPolicyURL' target='_blank' rel='nofollow noreferrer'>Privacy Policy</a>.",
                      {
                        "@privacyPolicyURL": `/${lang}${privacyLink}`
                      }
                    )}
                    value="1"
                    required
                  />
                </div>
              </div>
            )}
            <div className={gridClasses.row}>
              <div className={contentGridClass}>
                <ReCAPTCHAInput
                  name="reCAPTCHA"
                  label=""
                  setFieldValue={formikProps.setFieldValue}
                />
              </div>
            </div>
            <div className={`signup-actions ${gridClasses.row}`}>
              <div className={gridClasses.halfWithoutSidebar}>
                <button
                  className="button button--primary button--stretch"
                  type="submit"
                  disabled={formikProps.isSubmitting || !formikProps.isValid}
                >
                  {window.Drupal.t("Sign up")}
                </button>
              </div>
            </div>
          </Form>
        )}
      />
    </div>
  );
}

EmailSignupForm.propTypes = {
  client: PropTypes.func.isRequired,
  onComplete: PropTypes.func
};

EmailSignupForm.defaultProps = {
  onComplete: undefined
};

export default EmailSignupForm;
